import { useEffect, useRef } from "react";
import { DeviceDetector } from "../input";
import type { StreamStatus } from "..";
import { shouldSimulateMouseEvents } from "../utils";
import { requestKeyboardLock, requestPointerLock } from "../utils/screen";

interface UsePointerLockOnInputOptions {
  onLocked?: () => void;
  isStreamLoaded?: boolean;
  showVirtualGamepad?: boolean;
  streamStatus?: StreamStatus;
  isTV?: boolean;
}

/**
 * Request pointer lock once on the first user gesture (click, keydown, touch, or gamepad),
 */
export function usePointerLock(
  videoId: string,
  options?: UsePointerLockOnInputOptions,
) {
  const hasLockedRef = useRef(false);
  const needsReLockRef = useRef(false);
  const hasInputTriggeredRef = useRef(false);
  const pollingTimerRef = useRef<number | null>(null);
  const onLockedRef = useRef<(() => void) | undefined>(options?.onLocked);
  const streamStatusRef = useRef(options?.streamStatus);
  const isPseudoFullscreenRef = useRef(false);

  useEffect(() => {
    onLockedRef.current = options?.onLocked;
  }, [options?.onLocked]);

  useEffect(() => {
    streamStatusRef.current = options?.streamStatus;
  }, [options?.streamStatus]);

  useEffect(() => {
    // Skip pointer lock for TV devices - they use remote controls/gamepads, not mice
    if (options?.isTV) {
      return;
    }
    if (options?.showVirtualGamepad) return;

    const video = document.getElementById(videoId) as HTMLElement | null;
    if (!video) return;

    // Check if window is fullscreen-sized (pseudo-fullscreen)
    const checkPseudoFullscreen = () => {
      const isFullscreenSized =
        window.innerHeight >= window.screen.availHeight - 10 &&
        window.innerWidth >= window.screen.availWidth - 10;

      const wasFullscreen = isPseudoFullscreenRef.current;
      isPseudoFullscreenRef.current = isFullscreenSized;

      if (isFullscreenSized && !wasFullscreen) {
        hasInputTriggeredRef.current = false;
      } else if (!isFullscreenSized && wasFullscreen) {
        hasInputTriggeredRef.current = false;
      }
    };

    const requestLock = async () => {
      if (
        document.pointerLockElement === video ||
        DeviceDetector.isTouchDevice()
      )
        return;

      await Promise.all([
        requestPointerLock({
          element: video,
          onError: () => {
            hasInputTriggeredRef.current = false;
          },
        }),
        requestKeyboardLock(),
      ]);
    };

    const handlePointerLockChange = () => {
      const locked = document.pointerLockElement === video;
      if (locked) {
        hasLockedRef.current = true;
        needsReLockRef.current = false;
        hasInputTriggeredRef.current = false;
        stopGamepadPolling();
        removeGestureListeners();
        onLockedRef.current?.();
      } else {
        addGestureListeners();
      }
    };

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        isPseudoFullscreenRef.current = false;
      } else {
        checkPseudoFullscreen();
      }
    };

    const handleResize = () => {
      if (!document.fullscreenElement) {
        checkPseudoFullscreen();
      }
    };

    const handleUserGesture = (
      event?: MouseEvent | KeyboardEvent | TouchEvent,
    ) => {
      if (hasInputTriggeredRef.current || !options?.isStreamLoaded) return;
      const video = document.getElementById(videoId) as HTMLElement | null;

      // Skip system keys that shouldn't trigger pointer lock
      if (event instanceof KeyboardEvent) {
        const systemKeys = [
          "Escape",
          "F11",
          "Tab",
          "Alt",
          "Control",
          "Meta",
          "Shift",
        ];
        if (systemKeys.includes(event.key)) {
          return;
        }
      } else {
        // Skip DOM target check for keyboard events so key presses (even when
        // focus isn't on the video element) can still trigger pointer lock.
        const targetNode = (event?.target as Node) || undefined;
        if (targetNode && video && !video.contains(targetNode)) {
          return;
        }
      }

      hasInputTriggeredRef.current = true;

      // Request pointer lock for both actual fullscreen and pseudo-fullscreen
      if (
        (document.fullscreenElement || isPseudoFullscreenRef.current) &&
        !document.pointerLockElement &&
        (event as KeyboardEvent)?.key !== "Escape" &&
        (event as KeyboardEvent)?.key !== "F12"
      ) {
        requestLock();
        if (needsReLockRef.current) {
          needsReLockRef.current = false;
        }
      }
    };

    const handleBlur = () => {
      if (
        options?.isStreamLoaded &&
        (document.fullscreenElement || isPseudoFullscreenRef.current)
      ) {
        needsReLockRef.current = true;
        hasInputTriggeredRef.current = false;
        addGestureListeners();
        startGamepadPolling();
      }
    };

    // --- Gesture listeners ---
    const addGestureListeners = () => {
      document.addEventListener("click", handleUserGesture);
      document.addEventListener("keydown", handleUserGesture);
      document.addEventListener("touchstart", handleUserGesture, {
        passive: true,
      });
    };

    const removeGestureListeners = () => {
      document.removeEventListener("click", handleUserGesture);
      document.removeEventListener("keydown", handleUserGesture);
      document.removeEventListener("touchstart", handleUserGesture);
    };

    // --- Gamepad polling (stops when not needed) ---
    const pollGamepad = () => {
      if (hasInputTriggeredRef.current) return;
      const gamepads = navigator.getGamepads?.() || [];
      for (const gp of gamepads) {
        if (!gp) continue;
        if (gp.buttons.some((b) => b.pressed)) {
          if (!shouldSimulateMouseEvents(streamStatusRef.current)) {
            handleUserGesture();
            return;
          }
        }
      }
      pollingTimerRef.current = window.setTimeout(pollGamepad, 100);
    };

    const startGamepadPolling = () => {
      if (pollingTimerRef.current != null) return;
      pollingTimerRef.current = window.setTimeout(pollGamepad, 100);
    };

    const stopGamepadPolling = () => {
      if (pollingTimerRef.current != null) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    // --- Initial attach ---
    addGestureListeners();

    if (
      typeof navigator.getGamepads === "function" &&
      Array.from(navigator.getGamepads() || []).some((gp) => gp)
    ) {
      startGamepadPolling();
    }

    checkPseudoFullscreen();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        checkPseudoFullscreen();
      } else {
        checkPseudoFullscreen();
      }
    };

    document.addEventListener("pointerlockchange", handlePointerLockChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("resize", handleResize);
    window.addEventListener("blur", handleBlur);

    return () => {
      removeGestureListeners();
      stopGamepadPolling();
      document.removeEventListener(
        "pointerlockchange",
        handlePointerLockChange,
      );
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("blur", handleBlur);
    };
  }, [videoId, options?.isStreamLoaded]);
}
